import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

export type ThemeName = "defaulted" | "smoked" | "spectre" | "reel";

const THEME_COOKIE = "qb_theme";

const themeValues: ThemeName[] = ["defaulted", "smoked", "spectre", "reel"];

const getCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.split("=")[1];
};

const parseTheme = (value?: string | null): ThemeName | undefined => {
  if (!value) return undefined;
  const candidate = value.trim().toLowerCase() as ThemeName;
  return themeValues.includes(candidate) ? candidate : undefined;
};

type ThemeClassTarget = { classList: Pick<DOMTokenList, "add" | "remove"> };

const themeBody = (): ThemeClassTarget | undefined =>
  typeof document === "undefined" ? undefined : document.body;

/** Puts exactly one theme class on the themed element, which every CSS token reads from. */
export const applyThemeClass = (theme: ThemeName, target: ThemeClassTarget | undefined = themeBody()) => {
  target?.classList.remove(...themeValues.map((value) => `theme-${value}`));
  target?.classList.add(`theme-${theme}`);
};

/**
 * Changes the theme with the class applied FIRST. Charts resolve their colours
 * from the body's CSS tokens while rendering, memoised on the theme name, so the
 * class must already match when the state change triggers that render; applying
 * it in an effect afterwards left them painted in the previous theme.
 */
export const commitTheme = (
  theme: ThemeName,
  setState: (theme: ThemeName) => void,
  target: ThemeClassTarget | undefined = themeBody(),
) => {
  applyThemeClass(theme, target);
  setState(theme);
};

const ThemeContext = createContext<{
  theme: ThemeName;
  setTheme: (theme: ThemeName) => Promise<void>;
  isOverride: boolean;
} | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const overrideTheme = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return parseTheme(params.get("theme"));
  }, [location.search]);

  const [theme, setThemeState] = useState<ThemeName>(() => {
    // Before the first render of any child: the boot script applied the saved
    // theme, which a `?theme=` override can differ from.
    const initial = overrideTheme ?? parseTheme(getCookie(THEME_COOKIE)) ?? "spectre";
    applyThemeClass(initial);
    return initial;
  });

  useEffect(() => {
    if (overrideTheme && overrideTheme !== theme) {
      commitTheme(overrideTheme, setThemeState);
    }
  }, [overrideTheme, theme]);

  const setTheme = async (nextTheme: ThemeName) => {
    commitTheme(nextTheme, setThemeState);

    if (overrideTheme) {
      const params = new URLSearchParams(location.search);
      params.delete("theme");
      navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`, {
        replace: true
      });
    }

    try {
      await fetch("/theme", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme: nextTheme })
      });
    } catch {
      // non-blocking
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isOverride: Boolean(overrideTheme) }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
};
